import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Card } from "@/shared/components/ui/card";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { useQpmOfferShowQuery } from "@/services/backend/qpm/offers/api/offer-api";
import { OfferNavLinkType } from "@/services/backend/qpm/offers/types/offer-enum";
import { DeleteOfferDialog } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/delete-offer-dialog";
import { OfferPdf } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-pdf";
import {
  OfferContextProvider,
  useOfferContext,
} from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";
import {
  CollapsibleDocumentPageNavigation,
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigationOutlet,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { SignatureButton } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/signature-button";
import { fieldErrorsForPrefix } from "@/shared/lib/errors/validation-error";
import { Offer } from "@/services/backend/qpm/offers/types/offer";
import { VerifyOfferDocumentButton } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/verify-offer-document-button";
import { SetOfferImmutableButton } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/set-offer-immutable-button";
import { SelectCalculationButtonComponent } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/select-calculation-component";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Button } from "@/shared/components/ui/button";
import { MoreHorizontal } from "lucide-react";

function useCompactHeader(): boolean {
  const [isCompact, setIsCompact] = useState(
    () => window.matchMedia("(max-width: 1200px)").matches,
  );

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1200px)");
    const onChange = (event: MediaQueryListEvent) => {
      setIsCompact(event.matches);
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return isCompact;
}

function OfferNavigation() {
  const { pathname } = useLocation();
  const { validationError } = useOfferContext();

  const countErrors = (prefix: string) =>
    Object.values(fieldErrorsForPrefix(validationError, prefix) ?? {}).reduce(
      (sum, errs) => sum + (errs?.length ?? 0),
      0,
    );

  const countErrorsForPrefixes = (prefixes: string[]) => {
    const merged = Object.fromEntries(
      prefixes.flatMap((prefix) =>
        Object.entries(fieldErrorsForPrefix(validationError, prefix) ?? {}).map(
          ([field, errs]) => [`${prefix}${field}`, errs],
        ),
      ),
    );

    return Object.values(merged).reduce(
      (sum, errs) => sum + (errs?.length ?? 0),
      0,
    );
  };

  const links = [
    {
      to: OfferNavLinkType.Overview,
      label: t("Übersicht"),
      count:
        countErrors("date") +
        countErrors("validUntilDate") +
        countErrors("flyExterminatorMaintenance.discount") +
        countErrorsForPrefixes([
          "documentProfile.",
          "documentProfile.offerType",
          "documentProfile.mainOfferKind",
          "documentProfile.priorIoTPresenceState",
          "documentProfile.tableMode",
          "documentProfile.ioTInTableMode",
          "documentProfile.ioTRentalContractMode",
          "documentProfile.referencedContractDate",
        ]),
    },
    {
      to: OfferNavLinkType.Contract,
      label: t("Vertrag"),
      count:
        countErrors("certifiedTrainingCoursePrice") +
        countErrors("contractStartMode") +
        countErrors("contractStartDate") +
        countErrors("contractTermMode") +
        countErrors("contractTermText") +
        countErrors("contractTermUnlimitedText") +
        countErrors("contractTermContractText") +
        countErrors("contractAdditionalText") +
        countErrors("ioTAdditionalTrapRentEnabled") +
        countErrors("ioTAdditionalTrapRentMode") +
        countErrors("ioTAdditionalTrapRentPrice") +
        countErrors("ioTAdditionalTrapRentMonitorIds") +
        countErrors("ioTTargetOrganismSelection") +
        countErrorsForPrefixes([
          "documentProfile.contractAgainstText",
          "documentProfile.targetAreaScope",
          "documentProfile.extension.contractAgainstText",
          "documentProfile.extension.targetAreaScope",
          "documentProfile.ioTRentalServiceText",
          "documentProfile.extension.ioTRentalServiceText",
        ]),
    },
    {
      to: OfferNavLinkType.ServiceProgram,
      label: t("Leistungsprogramm"),
      count: countErrors("offerConfigSnapshot.settings.serviceProgramEntries"),
    },
    {
      to: OfferNavLinkType.Pests,
      label: t("Schädlinge"),
      count: countErrors("targetOrganismSelection"),
    },
    {
      to: OfferNavLinkType.Addresses,
      label: t("Adressen"),
      count: countErrors("postalAddress.") + countErrors("serviceAddress."),
    },
    {
      to: OfferNavLinkType.Representative,
      label: t("Betreuer"),
      count: countErrors("representative."),
    },
    {
      to: OfferNavLinkType.CoverLetter,
      label: t("Anschreiben"),
      count:
        countErrors("coverLetterText.") +
        countErrors("offerConfigSnapshot.") +
        countErrors("offerConfigSnapshot.textBlocks."),
    },
    {
      to: OfferNavLinkType.TextBlocks,
      label: t("Allgemeine Textbausteine"),
      count: 0,
    },
    {
      to: OfferNavLinkType.AdditionalInfo,
      label: t("Zusatz"),
      count: 0,
    },
  ];

  const activeLink =
    links.find((link) => pathname.endsWith(`/${link.to}`)) ?? null;

  return (
    <CollapsibleDocumentPageNavigation
      label={t("Navigation")}
      currentLabel={activeLink?.label}
      maxVisibleTabs={4}
      priorityTabs={[
        OfferNavLinkType.Overview,
        OfferNavLinkType.Addresses,
        OfferNavLinkType.Representative,
        OfferNavLinkType.CoverLetter,
      ]}
      links={links.map((link) => ({
        to: link.to,
        label: link.label,
        showError: link.count > 0,
        errorCount: link.count > 0 ? link.count : undefined,
      }))}
    />
  );
}

export function OfferRoute() {
  const params = useParams();
  const { offerId } = params;

  const {
    data: offer,
    isLoading,
    error,
  } = useQpmOfferShowQuery({ id: offerId! }, { skip: !offerId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  return (
    offer && (
      <PersistenceContextProvider>
        <OfferContextProvider offer={offer}>
          <OfferPage offer={offer} />
        </OfferContextProvider>
      </PersistenceContextProvider>
    )
  );
}

function OfferPage({ offer }: { offer: Offer }) {
  const { validationError } = useOfferContext();
  const isCompactHeader = useCompactHeader();

  const hasSignatureIdError =
    Object.keys(fieldErrorsForPrefix(validationError, "signatureId") ?? {})
      .length > 0;

  return (
    <DocumentPage>
      <DocumentPageHeader className="block">
        {isCompactHeader ? (
          <div className="grid gap-2">
            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2">
              <div className="min-w-0">
                <OfferNavigation />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreHorizontal className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="flex flex-col gap-2 p-1">
                    <DeleteOfferDialog
                      offerId={offer.id}
                      disabled={offer.immutable}
                      triggerLabel={t("Angebot löschen")}
                    />
                    <VerifyOfferDocumentButton
                      offerId={offer.id}
                      disabled={offer.immutable}
                      triggerLabel={t("Vollständigkeit prüfen")}
                    />
                    <SetOfferImmutableButton
                      offerId={offer.id}
                      immutable={offer.immutable}
                      triggerLabel={t("Angebot festschreiben")}
                    />
                    <SignatureButton
                      offer={offer}
                      name={offer.representative.name}
                      showError={hasSignatureIdError}
                      disabled={offer.immutable}
                      triggerLabel={t("Unterschreiben")}
                    />
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              <OfferPdf offer={offer} />
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <span className="text-sm">{t("Kalkulationsgrundlage")}:</span>
                {offer.processId && (
                  <SelectCalculationButtonComponent
                    offerId={offer.id}
                    processId={offer.processId}
                    selectedCalculationId={offer.linkedCalculationId}
                  />
                )}
              </div>
              <PersistenceStateUsingContext className="shrink-0" />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <OfferNavigation />
            </div>
            <DocumentPageActions>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm">{t("Kalkulationsgrundlage")}:</span>
                {offer.processId && (
                  <SelectCalculationButtonComponent
                    offerId={offer.id}
                    processId={offer.processId}
                    selectedCalculationId={offer.linkedCalculationId}
                  />
                )}
              </div>
              <DeleteOfferDialog
                offerId={offer.id}
                disabled={offer.immutable}
              />
              <VerifyOfferDocumentButton
                offerId={offer.id}
                disabled={offer.immutable}
              />
              <SetOfferImmutableButton
                offerId={offer.id}
                immutable={offer.immutable}
              />
              <SignatureButton
                offer={offer}
                name={offer.representative.name}
                showError={hasSignatureIdError}
                disabled={offer.immutable}
              />
              <OfferPdf offer={offer} />
              <PersistenceStateUsingContext className="mr-2" />
            </DocumentPageActions>
          </div>
        )}
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
